'use client';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@workspace/ui/components/alert-dialog';
import { Button } from '@workspace/ui/components/button';
import { Checkbox } from '@workspace/ui/components/checkbox';
import { AlertDialog as ErrorAlertDialog } from '@workspace/ui/components/custom/alert-dialog';
import { TERMS_ACCEPTANCE_COPY, TERMS_AND_CONDITIONS_DOCUMENT_URL } from '@workspace/ui/lib';
import { ClipboardList, SquareArrowOutUpRight } from 'lucide-react';
import { useState } from 'react';

interface TermsAcceptanceDialogProps {
  open: boolean;
  documentUrl?: string | null;
  isPending: boolean;
  isLoggingOut: boolean;
  onAccept: () => void;
  onLogout: () => void;
  title: string;
  description: string;
  documentLabel: string;
  checkboxLabel: string;
  buttonLabel: string;
  buttonLoadingLabel: string;
  logoutLabel: string;
  logoutLoadingLabel: string;
}

export function TermsAcceptanceDialog({
  open,
  documentUrl,
  isPending,
  isLoggingOut,
  onAccept,
  onLogout,
  title,
  description,
  documentLabel,
  checkboxLabel,
  buttonLabel,
  buttonLoadingLabel,
  logoutLabel,
  logoutLoadingLabel,
}: TermsAcceptanceDialogProps) {
  const [hasAgreed, setHasAgreed] = useState(false);
  const resolvedDocumentUrl = documentUrl || TERMS_AND_CONDITIONS_DOCUMENT_URL;
  const isBusy = isPending || isLoggingOut;

  return (
    <AlertDialog open={open}>
      <AlertDialogContent hideCloseButton onEscapeKeyDown={(event) => event.preventDefault()}>
        <ClipboardList className="size-18.5 text-pink-400" strokeWidth={1.5} />
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
        <a
          href={resolvedDocumentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-grey-0 dark:bg-grey-900 border-grey-300 dark:border-grey-700 flex h-12 w-full items-center justify-between rounded-2xl border px-4"
        >
          <p className="text-base font-medium text-neutral-200 dark:text-neutral-100">
            {documentLabel}
          </p>
          <SquareArrowOutUpRight className="size-4 shrink-0 text-neutral-200 dark:text-neutral-100" />
        </a>
        <label htmlFor="terms-acceptance-agree" className="flex cursor-pointer items-center gap-2">
          <Checkbox
            id="terms-acceptance-agree"
            checked={hasAgreed}
            onCheckedChange={(checked) => setHasAgreed(checked === true)}
            disabled={isBusy}
            className="h-5 w-5"
          />
          <p className="text-sm font-medium text-neutral-200 dark:text-neutral-100">
            {checkboxLabel}
          </p>
        </label>
        <AlertDialogFooter>
          <Button
            type="button"
            className="w-full"
            disabled={!hasAgreed || isLoggingOut}
            isLoading={isPending}
            loadingText={buttonLoadingLabel}
            onClick={onAccept}
          >
            {buttonLabel}
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            disabled={isPending}
            isLoading={isLoggingOut}
            loadingText={logoutLoadingLabel}
            onClick={onLogout}
          >
            {logoutLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface TermsAcceptanceErrorDialogProps {
  isRetrying: boolean;
  isLoggingOut: boolean;
  onRetry: () => void;
  onLogout: () => void;
}

export function TermsAcceptanceErrorDialog({
  isRetrying,
  isLoggingOut,
  onRetry,
  onLogout,
}: TermsAcceptanceErrorDialogProps) {
  return (
    <ErrorAlertDialog
      open
      variant="error"
      title={TERMS_ACCEPTANCE_COPY.loadErrorTitle}
      description={TERMS_ACCEPTANCE_COPY.loadErrorDescription}
    >
      <Button
        type="button"
        className="w-full"
        disabled={isLoggingOut}
        isLoading={isRetrying}
        loadingText={TERMS_ACCEPTANCE_COPY.retryLoading}
        onClick={onRetry}
      >
        {TERMS_ACCEPTANCE_COPY.retry}
      </Button>
      <Button
        type="button"
        variant="secondary"
        className="w-full"
        disabled={isRetrying}
        isLoading={isLoggingOut}
        loadingText={TERMS_ACCEPTANCE_COPY.logoutLoading}
        onClick={onLogout}
      >
        {TERMS_ACCEPTANCE_COPY.logout}
      </Button>
    </ErrorAlertDialog>
  );
}
